import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import useAuth from '../hooks/useAuth';
import {
  getContacts,
  removeContact,
  removeFamilyMember,
  uploadContactImage,
  upsertContact,
  upsertFamilyMember,
} from '../apis/repositories/contactsRepository';
import {
  getCommitteeAssignments,
  removeCommitteeAssignment,
  upsertCommitteeAssignment,
} from 'apis/repositories/committeeRepository';
import {
  createAnnouncement,
  getAnnouncements,
  removeAnnouncement,
} from 'apis/repositories/announcementsRepository';
import {
  listUserAccessRecords,
  updateUserAccessStatus,
  type UserAccessRecord,
  type UserAccessStatus,
} from 'apis/repositories/userAccessRepository';
import type {
  Announcement,
  CommitteeAssignment,
  FamilyContact,
  FamilyMember,
  MemberLookupItem,
} from 'types/domain';
import { resolveContactImageSrc } from 'utils/image';
import styles from './AdminPanel.module.css';

type FamilyContactDraft = Omit<FamilyContact, 'id'> & { id: string | number };
type FamilyMemberDraft = Omit<FamilyMember, 'id'> & { id: string | number };
type AdminMode = 'add-family' | 'edit-family';
type AdminSection = 'families' | 'committee' | 'notifications' | 'access';
type CommitteeDraft = {
  id: string;
  role: string;
  memberRef: string;
  order: string | number;
};

const emptyContact: FamilyContactDraft = {
  id: '',
  photoFile: '',
  familyHeadName: '',
  gotraDetail: '',
  address: '',
  mobile: '',
  occupationName: '',
  occupationDesciption: '',
  occupationAddress: '',
  familyDetails: [],
};

const emptyMember: FamilyMemberDraft = {
  id: '',
  photoFile: '',
  name: '',
  relation: '',
  birthDate: '',
  bloodGroup: '',
  mobile: '',
  alternateNumber: '',
  email: '',
  occupation: '',
  currentAddress: '',
};

const emptyCommitteeDraft: CommitteeDraft = {
  id: '',
  role: '',
  memberRef: '',
  order: '',
};

const AdminPanel = () => {
  const { isAdmin } = useAuth();
  const [section, setSection] = useState<AdminSection>('access');
  const [adminMode, setAdminMode] = useState<AdminMode>('add-family');
  const [contacts, setContacts] = useState<FamilyContact[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [contactDraft, setContactDraft] = useState<FamilyContactDraft>(emptyContact);
  const [contactPhotoFile, setContactPhotoFile] = useState<File | null>(null);
  const [removeContactPhoto, setRemoveContactPhoto] = useState(false);

  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [memberFormOpen, setMemberFormOpen] = useState(false);
  const [memberDraft, setMemberDraft] = useState<FamilyMemberDraft>(emptyMember);
  const [memberPhotoFile, setMemberPhotoFile] = useState<File | null>(null);
  const [removeMemberPhoto, setRemoveMemberPhoto] = useState(false);

  const [committeeAssignments, setCommitteeAssignments] = useState<CommitteeAssignment[]>([]);
  const [committeeDraft, setCommitteeDraft] = useState<CommitteeDraft>(emptyCommitteeDraft);
  const [committeeFormOpen, setCommitteeFormOpen] = useState(false);

  const [notifications, setNotifications] = useState<Announcement[]>([]);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationExpiry, setNotificationExpiry] = useState('');

  const [userAccessRecords, setUserAccessRecords] = useState<UserAccessRecord[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isEditMode = useMemo(() => adminMode === 'edit-family', [adminMode]);
  const isMemberEditMode = useMemo(() => Boolean(selectedMemberId), [selectedMemberId]);

  const allMembers = useMemo<MemberLookupItem[]>(() => {
    const members: MemberLookupItem[] = [];

    contacts.forEach((contact) => {
      (contact.familyDetails || []).forEach((member) => {
        members.push({
          contactId: Number(contact.id),
          memberId: Number(member.id),
          memberName: member.name,
          familyHeadName: contact.familyHeadName,
          relation: member.relation,
          photoFile: member.photoFile,
        });
      });
    });

    return members;
  }, [contacts]);

  const canPostNotification = useMemo(
    () => notificationTitle.trim() && notificationMessage.trim(),
    [notificationTitle, notificationMessage]
  );

  const selectedContactPhotoPreview = useMemo(() => {
    if (removeContactPhoto) {
      return '';
    }

    if (contactPhotoFile) {
      return URL.createObjectURL(contactPhotoFile);
    }

    return resolveContactImageSrc(contactDraft.photoFile);
  }, [contactPhotoFile, contactDraft.photoFile, removeContactPhoto]);

  const selectedMemberPhotoPreview = useMemo(() => {
    if (removeMemberPhoto) {
      return '';
    }

    if (memberPhotoFile) {
      return URL.createObjectURL(memberPhotoFile);
    }

    return resolveContactImageSrc(memberDraft.photoFile);
  }, [memberDraft.photoFile, memberPhotoFile, removeMemberPhoto]);

  useEffect(() => {
    return () => {
      if (contactPhotoFile && selectedContactPhotoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(selectedContactPhotoPreview);
      }
      if (memberPhotoFile && selectedMemberPhotoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(selectedMemberPhotoPreview);
      }
    };
  }, [contactPhotoFile, memberPhotoFile, selectedContactPhotoPreview, selectedMemberPhotoPreview]);

  const toNumberOrEmpty = (value: string | number): number | '' => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : '';
  };

  const getResolvedContactId = (): number => {
    const parsed = Number(contactDraft.id);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }

    return Date.now();
  };

  const loadContacts = async () => {
    setLoading(true);
    setError('');

    try {
      const list = await getContacts();
      const sorted = [...list].sort((a, b) => a.familyHeadName.localeCompare(b.familyHeadName));
      setContacts(sorted);
    } catch (apiError) {
      console.error(apiError);
      setError('Unable to load contacts.');
    } finally {
      setLoading(false);
    }
  };

  const loadCommitteeAssignments = async () => {
    try {
      const list = await getCommitteeAssignments();
      setCommitteeAssignments(list);
    } catch (requestError) {
      console.error(requestError);
      setError('Unable to load committee assignments.');
    }
  };

  const loadNotifications = async () => {
    try {
      const list = await getAnnouncements();
      setNotifications(list);
    } catch (requestError) {
      console.error(requestError);
      setError('Unable to load notifications.');
    }
  };

  const loadUserAccessRecords = async () => {
    try {
      const list = await listUserAccessRecords();
      setUserAccessRecords(list);
    } catch (requestError) {
      console.error(requestError);
      setError('Unable to load user access requests.');
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    loadContacts();
    loadCommitteeAssignments();
    loadNotifications();
    loadUserAccessRecords();
  }, [isAdmin]);

  const resetFamilyDraft = () => {
    setSelectedId('');
    setContactDraft(emptyContact);
    setContactPhotoFile(null);
    setRemoveContactPhoto(false);
    setSelectedMemberId('');
    setMemberFormOpen(false);
    setMemberDraft(emptyMember);
    setMemberPhotoFile(null);
    setRemoveMemberPhoto(false);
  };

  const handleSwitchMode = (mode: AdminMode) => {
    setAdminMode(mode);
    setError('');
    setSuccessMessage('');
    resetFamilyDraft();
  };

  const handleSelectContact = (value: string) => {
    setSelectedId(value);
    setError('');
    setSuccessMessage('');
    setSelectedMemberId('');
    setMemberFormOpen(false);
    setMemberDraft(emptyMember);
    setMemberPhotoFile(null);
    setRemoveMemberPhoto(false);
    setContactPhotoFile(null);
    setRemoveContactPhoto(false);

    if (!value) {
      setContactDraft(emptyContact);
      return;
    }

    const selectedContact = contacts.find((contact) => String(contact.id) === String(value));

    if (!selectedContact) {
      setContactDraft(emptyContact);
      return;
    }

    setContactDraft({ ...selectedContact });
  };

  const handleDraftChange = (field: keyof FamilyContactDraft) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setContactDraft((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const openMemberFormForAdd = () => {
    setSelectedMemberId('');
    setMemberDraft(emptyMember);
    setMemberPhotoFile(null);
    setRemoveMemberPhoto(false);
    setMemberFormOpen(true);
  };

  const openMemberFormForEdit = (memberId: string) => {
    const selectedMember = (contactDraft.familyDetails || []).find(
      (member) => String(member.id) === String(memberId)
    );

    if (!selectedMember) {
      return;
    }

    setSelectedMemberId(memberId);
    setMemberDraft({ ...selectedMember });
    setMemberPhotoFile(null);
    setRemoveMemberPhoto(false);
    setMemberFormOpen(true);
  };

  const handleMemberDraftChange = (field: keyof FamilyMemberDraft) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setMemberDraft((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCommitteeDraftChange =
    (field: keyof CommitteeDraft) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setCommitteeDraft((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const resolveMemberRef = (memberRef: string): { contactId: number; memberId: number } => {
    const [contactId, memberId] = memberRef.split(':').map(Number);
    return {
      contactId: Number(contactId) || 0,
      memberId: Number(memberId) || 0,
    };
  };

  const openCommitteeFormForAdd = () => {
    setCommitteeDraft(emptyCommitteeDraft);
    setCommitteeFormOpen(true);
  };

  const openCommitteeFormForEdit = (assignmentId: string) => {
    const assignment = committeeAssignments.find((item) => item.id === assignmentId);
    if (!assignment) {
      return;
    }

    setCommitteeDraft({
      id: assignment.id,
      role: assignment.role,
      memberRef: `${assignment.contactId}:${assignment.memberId}`,
      order: assignment.order,
    });
    setCommitteeFormOpen(true);
  };

  const handleSaveCommitteeMember = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const { contactId, memberId } = resolveMemberRef(committeeDraft.memberRef);
      if (!contactId || !memberId || !committeeDraft.role.trim()) {
        throw new Error('Please select a member and add a role for committee assignment.');
      }

      await upsertCommitteeAssignment({
        id: committeeDraft.id || undefined,
        role: committeeDraft.role.trim(),
        contactId,
        memberId,
        order: Number(committeeDraft.order) || 0,
      });
      await loadCommitteeAssignments();
      setCommitteeFormOpen(false);
      setCommitteeDraft(emptyCommitteeDraft);
      setSuccessMessage('Committee assignment saved successfully.');
    } catch (apiError) {
      console.error(apiError);
      setError(apiError instanceof Error ? apiError.message : 'Unable to save committee assignment.');
    } finally {
      setLoading(false);
    }
  };

  const toIsoFromDateTimeLocal = (value: string): string => {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString();
  };

  const handleCreateNotification = async () => {
    if (!canPostNotification) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await createAnnouncement({
        title: notificationTitle.trim(),
        message: notificationMessage.trim(),
        createdBy: 'Admin',
        expiresAt: toIsoFromDateTimeLocal(notificationExpiry),
      });

      setNotificationTitle('');
      setNotificationMessage('');
      setNotificationExpiry('');
      await loadNotifications();
      setSuccessMessage('Notification posted successfully.');
    } catch (requestError) {
      console.error(requestError);
      setError('Unable to create notification.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotification = async (announcementId: string) => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await removeAnnouncement(announcementId);
      await loadNotifications();
      setSuccessMessage('Notification deleted successfully.');
    } catch (requestError) {
      console.error(requestError);
      setError('Unable to delete notification.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserAccess = async (uid: string, status: UserAccessStatus) => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await updateUserAccessStatus({
        uid,
        status,
        reviewedBy: 'admin',
      });
      await loadUserAccessRecords();
      setSuccessMessage(`User access ${status}.`);
    } catch (requestError) {
      console.error(requestError);
      setError('Unable to update user access status.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCommitteeMember = async (assignmentId: string) => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await removeCommitteeAssignment(assignmentId);
      await loadCommitteeAssignments();
      if (committeeDraft.id === assignmentId) {
        setCommitteeDraft(emptyCommitteeDraft);
        setCommitteeFormOpen(false);
      }
      setSuccessMessage('Committee assignment removed successfully.');
    } catch (apiError) {
      console.error(apiError);
      setError('Unable to remove committee assignment.');
    } finally {
      setLoading(false);
    }
  };

  const resolveContactPhotoToSave = async (contactId: number): Promise<string> => {
    if (removeContactPhoto) {
      return '';
    }

    if (contactPhotoFile) {
      return uploadContactImage({
        contactId,
        scope: 'family',
        file: contactPhotoFile,
      });
    }

    return contactDraft.photoFile;
  };

  const resolveMemberPhotoToSave = async (
    contactId: number,
    memberId: number
  ): Promise<string> => {
    if (removeMemberPhoto) {
      return '';
    }

    if (memberPhotoFile) {
      return uploadContactImage({
        contactId,
        scope: 'member',
        memberId,
        file: memberPhotoFile,
      });
    }

    return memberDraft.photoFile;
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const resolvedId = getResolvedContactId();
      const photoFile = await resolveContactPhotoToSave(resolvedId);
      const payload: FamilyContact = {
        ...contactDraft,
        id: resolvedId,
        photoFile,
        familyDetails: Array.isArray(contactDraft.familyDetails) ? contactDraft.familyDetails : [],
      };

      await upsertContact(payload);
      await loadContacts();
      setContactPhotoFile(null);
      setRemoveContactPhoto(false);
      handleSelectContact(String(payload.id));
      setSuccessMessage('Family details saved successfully.');
      setLoading(false);
    } catch (apiError) {
      console.error(apiError);
      setError('Unable to save family details.');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await removeContact(getResolvedContactId());
      await loadContacts();
      resetFamilyDraft();
      setSuccessMessage('Family deleted successfully.');
      setLoading(false);
    } catch (apiError) {
      console.error(apiError);
      setError('Unable to delete family.');
      setLoading(false);
    }
  };

  const handleSaveMember = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const resolvedMemberId = toNumberOrEmpty(memberDraft.id) || Date.now();
      const resolvedContactId = getResolvedContactId();
      const photoFile = await resolveMemberPhotoToSave(
        resolvedContactId,
        Number(resolvedMemberId)
      );

      const payload: FamilyMember = {
        ...memberDraft,
        id: resolvedMemberId,
        photoFile,
      };

      if (isEditMode) {
        const nextMembers = await upsertFamilyMember(resolvedContactId, payload);
        setContactDraft((prev) => ({
          ...prev,
          familyDetails: nextMembers,
        }));
      } else {
        const existingMembers = contactDraft.familyDetails || [];
        const memberIndex = existingMembers.findIndex((item) => Number(item.id) === Number(payload.id));
        const nextMembers = [...existingMembers];

        if (memberIndex >= 0) {
          nextMembers[memberIndex] = payload;
        } else {
          nextMembers.push(payload);
        }

        setContactDraft((prev) => ({
          ...prev,
          familyDetails: nextMembers,
        }));
      }

      setSelectedMemberId(String(payload.id));
      setMemberFormOpen(false);
      setMemberPhotoFile(null);
      setRemoveMemberPhoto(false);
      setSuccessMessage('Family member saved successfully.');
      setLoading(false);
    } catch (apiError) {
      console.error(apiError);
      setError('Unable to save family member.');
      setLoading(false);
    }
  };

  const handleDeleteMember = async () => {
    if (!isMemberEditMode) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const memberId = Number(memberDraft.id);

      if (isEditMode) {
        const nextMembers = await removeFamilyMember(getResolvedContactId(), memberId);
        setContactDraft((prev) => ({
          ...prev,
          familyDetails: nextMembers,
        }));
      } else {
        setContactDraft((prev) => ({
          ...prev,
          familyDetails: (prev.familyDetails || []).filter((member) => Number(member.id) !== memberId),
        }));
      }

      setSelectedMemberId('');
      setMemberFormOpen(false);
      setMemberDraft(emptyMember);
      setMemberPhotoFile(null);
      setRemoveMemberPhoto(false);
      setSuccessMessage('Family member deleted successfully.');
      setLoading(false);
    } catch (apiError) {
      console.error(apiError);
      setError('Unable to delete family member.');
      setLoading(false);
    }
  };

  const handleFamilyImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setContactPhotoFile(file);
    setRemoveContactPhoto(false);
    event.target.value = '';
  };

  const handleMemberImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setMemberPhotoFile(file);
    setRemoveMemberPhoto(false);
    event.target.value = '';
  };

  const handleRemoveFamilyPhoto = () => {
    setContactPhotoFile(null);
    setRemoveContactPhoto(true);
    setContactDraft((prev) => ({
      ...prev,
      photoFile: '',
    }));
  };

  const handleRemoveMemberPhoto = () => {
    setMemberPhotoFile(null);
    setRemoveMemberPhoto(true);
    setMemberDraft((prev) => ({
      ...prev,
      photoFile: '',
    }));
  };

  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <div className={styles.heading}>Admin Console</div>
        <div className={styles.error}>You do not have permission to access this section.</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.heading}>Admin Console</div>

      <Tabs
        value={section}
        onChange={(_, value: AdminSection) => setSection(value)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        className={styles.tabBar}
      >
        <Tab icon={<MarkEmailReadOutlinedIcon />} iconPosition="start" label="User Access" value="access" />
        <Tab icon={<GroupsOutlinedIcon />} iconPosition="start" label="Manage Family" value="families" />
        <Tab icon={<GroupAddOutlinedIcon />} iconPosition="start" label="Manage Committee" value="committee" />
        <Tab
          icon={<NotificationsActiveOutlinedIcon />}
          iconPosition="start"
          label="Manage Notification"
          value="notifications"
        />
      </Tabs>

      {section === 'families' ? (
        <>
          <Tabs
            value={adminMode}
            onChange={(_, value: AdminMode) => handleSwitchMode(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            className={styles.subTabBar}
          >
            <Tab icon={<AddCircleOutlineOutlinedIcon />} iconPosition="start" label="Add A Family" value="add-family" />
            <Tab icon={<EditOutlinedIcon />} iconPosition="start" label="Edit A Family" value="edit-family" />
          </Tabs>

          {adminMode === 'edit-family' ? (
            <div className={styles.formCard}>
              <div className={styles.sectionHeading}>Choose Family</div>
              <TextField
                select
                label="Select Family"
                value={selectedId}
                size="small"
                onChange={(event) => handleSelectContact(event.target.value)}
                fullWidth
              >
                <MenuItem value="">Select a family</MenuItem>
                {contacts.map((contact) => (
                  <MenuItem key={contact.id} value={String(contact.id)}>
                    {contact.familyHeadName}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          ) : null}

          {(adminMode === 'add-family' || selectedId) ? (
            <>
              <div className={styles.formCard}>
                <div className={styles.sectionHeading}>Family Form</div>
                <TextField label="Family Head Name" size="small" value={contactDraft.familyHeadName} onChange={handleDraftChange('familyHeadName')} fullWidth />
                <TextField label="Gotra Detail" size="small" value={contactDraft.gotraDetail} onChange={handleDraftChange('gotraDetail')} fullWidth />
                <TextField label="Address" size="small" value={contactDraft.address} onChange={handleDraftChange('address')} fullWidth />
                <TextField label="Mobile" size="small" value={contactDraft.mobile} onChange={handleDraftChange('mobile')} fullWidth />
                <TextField label="Occupation Name" size="small" value={contactDraft.occupationName} onChange={handleDraftChange('occupationName')} fullWidth />
                <TextField
                  label="Occupation Description"
                  size="small"
                  value={contactDraft.occupationDesciption}
                  onChange={handleDraftChange('occupationDesciption')}
                  fullWidth
                />
                <TextField label="Occupation Address" size="small" value={contactDraft.occupationAddress} onChange={handleDraftChange('occupationAddress')} fullWidth />

                <div className={styles.sectionHeading}>Family Photo</div>
                {selectedContactPhotoPreview ? (
                  <img src={selectedContactPhotoPreview} alt="Family" className={styles.photoPreview} />
                ) : (
                  <div className={styles.emptyImage}>No photo selected</div>
                )}
                <div className={styles.actionRow}>
                  <Button component="label" variant="outlined" startIcon={<PhotoCameraOutlinedIcon />}>
                    <span>Choose Photo</span>
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={handleFamilyImageUpload}
                    />
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRemoveFamilyPhoto}
                    startIcon={<DeleteForeverOutlinedIcon />}
                  >
                    Remove Photo
                  </Button>
                </div>

                <div className={styles.actionRow}>
                  <Button variant="contained" onClick={handleSave} disabled={loading} startIcon={<SaveOutlinedIcon />}>
                    Save Family
                  </Button>
                  <Button
                    color="error"
                    onClick={handleDelete}
                    disabled={!isEditMode || loading}
                    startIcon={<DeleteForeverOutlinedIcon />}
                  >
                    Delete Family
                  </Button>
                  {loading ? <CircularProgress size={20} /> : null}
                </div>
              </div>

              <div className={styles.formCard}>
                <div className={styles.sectionHeading}>Family Members</div>
                <Button variant="outlined" onClick={openMemberFormForAdd} startIcon={<PersonAddAlt1OutlinedIcon />}>
                  Add Member
                </Button>

                <div className={styles.memberList}>
                  {(contactDraft.familyDetails || []).map((member) => (
                    <div className={styles.memberListItem} key={member.id}>
                      <div>
                        <div className={styles.memberName}>{member.name || `Member ${member.id}`}</div>
                        <div className={styles.memberMeta}>{member.relation || 'No relation added'}</div>
                      </div>
                      <div className={styles.memberActions}>
                        <IconButton onClick={() => openMemberFormForEdit(String(member.id))}>
                          <EditOutlinedIcon />
                        </IconButton>
                      </div>
                    </div>
                  ))}

                  {(contactDraft.familyDetails || []).length === 0 ? (
                    <div className={styles.emptyState}>No members added yet.</div>
                  ) : null}
                </div>
              </div>

              <Dialog
                open={memberFormOpen}
                onClose={() => setMemberFormOpen(false)}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle>{isMemberEditMode ? 'Edit Member' : 'Add Member'}</DialogTitle>
                <DialogContent className={styles.memberDialogContent}>
                  <TextField label="Name" size="small" value={memberDraft.name} onChange={handleMemberDraftChange('name')} fullWidth />
                  <TextField label="Relation" size="small" value={memberDraft.relation} onChange={handleMemberDraftChange('relation')} fullWidth />
                  <TextField label="Birth Date" size="small" value={memberDraft.birthDate} onChange={handleMemberDraftChange('birthDate')} fullWidth />
                  <TextField label="Blood Group" size="small" value={memberDraft.bloodGroup} onChange={handleMemberDraftChange('bloodGroup')} fullWidth />
                  <TextField label="Mobile" size="small" value={memberDraft.mobile} onChange={handleMemberDraftChange('mobile')} fullWidth />
                  <TextField label="Alternate Number" size="small" value={memberDraft.alternateNumber} onChange={handleMemberDraftChange('alternateNumber')} fullWidth />
                  <TextField label="Email" size="small" value={memberDraft.email} onChange={handleMemberDraftChange('email')} fullWidth />
                  <TextField label="Occupation" size="small" value={memberDraft.occupation} onChange={handleMemberDraftChange('occupation')} fullWidth />
                  <TextField
                    label="Current Address"
                    size="small"
                    value={memberDraft.currentAddress}
                    onChange={handleMemberDraftChange('currentAddress')}
                    fullWidth
                  />

                  <div className={styles.sectionHeading}>Member Photo</div>
                  {selectedMemberPhotoPreview ? (
                    <img src={selectedMemberPhotoPreview} alt="Member" className={styles.photoPreview} />
                  ) : (
                    <div className={styles.emptyImage}>No photo selected</div>
                  )}
                  <div className={styles.actionRow}>
                    <Button component="label" variant="outlined" startIcon={<PhotoCameraOutlinedIcon />}>
                      <span>Choose Photo</span>
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={handleMemberImageUpload}
                      />
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveMemberPhoto}
                      startIcon={<DeleteForeverOutlinedIcon />}
                    >
                      Remove Photo
                    </Button>
                  </div>
                </DialogContent>
                <DialogActions className={styles.memberDialogActions}>
                  <Button onClick={() => setMemberFormOpen(false)}>Cancel</Button>
                  <Button variant="contained" onClick={handleSaveMember} disabled={loading} startIcon={<SaveOutlinedIcon />}>
                    Save Member
                  </Button>
                  <Button
                    color="error"
                    onClick={handleDeleteMember}
                    disabled={!isMemberEditMode || loading}
                    startIcon={<DeleteForeverOutlinedIcon />}
                  >
                    Delete Member
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          ) : null}
        </>
      ) : section === 'committee' ? (
        <div className={styles.formCard}>
          <div className={styles.sectionHeading}>Committee Members</div>
          <Button variant="outlined" onClick={openCommitteeFormForAdd} startIcon={<GroupAddOutlinedIcon />}>
            Add Committee Member
          </Button>

          <div className={styles.memberList}>
            {committeeAssignments.map((assignment) => {
              const member = allMembers.find(
                (item) =>
                  Number(item.contactId) === Number(assignment.contactId) &&
                  Number(item.memberId) === Number(assignment.memberId)
              );

              return (
                <div className={styles.memberListItem} key={assignment.id}>
                  <div>
                    <div className={styles.memberName}>{assignment.role || 'Committee Member'}</div>
                    <div className={styles.memberMeta}>
                      {member ? `${member.memberName} - ${member.familyHeadName}` : 'Member not found'}
                    </div>
                  </div>
                  <div className={styles.memberActions}>
                    <IconButton onClick={() => openCommitteeFormForEdit(assignment.id)}>
                      <EditOutlinedIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteCommitteeMember(assignment.id)}>
                      <DeleteForeverOutlinedIcon />
                    </IconButton>
                  </div>
                </div>
              );
            })}

            {committeeAssignments.length === 0 ? (
              <div className={styles.emptyState}>No committee members assigned yet.</div>
            ) : null}
          </div>

          {committeeFormOpen ? (
            <>
              <div className={styles.sectionHeading}>Committee Assignment Form</div>
              <TextField
                select
                label="Select Member"
                value={committeeDraft.memberRef}
                size="small"
                onChange={handleCommitteeDraftChange('memberRef')}
                fullWidth
              >
                <MenuItem value="">Select member</MenuItem>
                {allMembers.map((member) => (
                  <MenuItem
                    key={`${member.contactId}:${member.memberId}`}
                    value={`${member.contactId}:${member.memberId}`}
                  >
                    {member.memberName} - {member.familyHeadName}
                  </MenuItem>
                ))}
              </TextField>
              <TextField label="Role" size="small" value={committeeDraft.role} onChange={handleCommitteeDraftChange('role')} fullWidth />
              <TextField label="Display Order" size="small" value={committeeDraft.order} onChange={handleCommitteeDraftChange('order')} fullWidth />

              <div className={styles.actionRow}>
                <Button variant="contained" onClick={handleSaveCommitteeMember} disabled={loading} startIcon={<SaveOutlinedIcon />}>
                  Save Committee Member
                </Button>
              </div>
            </>
          ) : null}
        </div>
      ) : section === 'notifications' ? (
        <div className={styles.formCard}>
          <div className={styles.sectionHeading}>Manage Notifications</div>
          <TextField
            label="Title"
            size="small"
            value={notificationTitle}
            onChange={(event) => setNotificationTitle(event.target.value)}
            fullWidth
          />
          <TextField
            label="Message"
            size="small"
            value={notificationMessage}
            onChange={(event) => setNotificationMessage(event.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField
            label="Expiry"
            type="datetime-local"
            size="small"
            value={notificationExpiry}
            onChange={(event) => setNotificationExpiry(event.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty to keep this visible until deleted"
          />
          <div className={styles.actionRow}>
            <Button variant="contained" onClick={handleCreateNotification} disabled={!canPostNotification || loading}>
              Post Notification
            </Button>
          </div>

          <div className={styles.memberList}>
            {notifications.map((item) => (
              <div key={item.id} className={styles.memberListItem}>
                <div>
                  <div className={styles.memberName}>{item.title}</div>
                  <div className={styles.memberMeta}>{item.message}</div>
                  <div className={styles.memberMeta}>
                    {item.expiresAt ? `Expires ${new Date(item.expiresAt).toLocaleString()}` : 'No expiry'}
                  </div>
                </div>
                <div className={styles.memberActions}>
                  <IconButton onClick={() => handleDeleteNotification(item.id)}>
                    <DeleteForeverOutlinedIcon />
                  </IconButton>
                </div>
              </div>
            ))}

            {notifications.length === 0 ? (
              <div className={styles.emptyState}>No notifications found.</div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className={styles.formCard}>
          <div className={styles.sectionHeading}>Approve / Reject User Access</div>

          <div className={styles.memberList}>
            {userAccessRecords.map((record) => (
              <div className={styles.memberListItem} key={record.uid}>
                <div>
                  <div className={styles.memberName}>{record.email || record.uid}</div>
                  <div className={styles.memberMeta}>Status: {record.status}</div>
                </div>
                <div className={styles.memberActions}>
                  <Button
                    size="small"
                    color="success"
                    variant={record.status === 'approved' ? 'contained' : 'outlined'}
                    disabled={loading || record.status === 'approved'}
                    onClick={() => handleUpdateUserAccess(record.uid, 'approved')}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant={record.status === 'rejected' ? 'contained' : 'outlined'}
                    disabled={loading || record.status === 'rejected'}
                    onClick={() => handleUpdateUserAccess(record.uid, 'rejected')}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}

            {userAccessRecords.length === 0 ? (
              <div className={styles.emptyState}>No user access requests found.</div>
            ) : null}
          </div>
        </div>
      )}

      {error ? <div className={styles.error}>{error}</div> : null}
      {successMessage ? <div className={styles.success}>{successMessage}</div> : null}
    </div>
  );
};

export default AdminPanel;
